import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Badge } from "primereact/badge";
import { Card } from "primereact/card";

const localizer = momentLocalizer(moment);

const DutyRosterCalendar = ({ dutyRoster, onStatusUpdate }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [showRosterDialog, setShowRosterDialog] = useState(false);
  const [showDayDialog, setShowDayDialog] = useState(false);
  const [dayRosters, setDayRosters] = useState([]);
  const [view, setView] = useState("month");
  const [date, setDate] = useState(new Date());
  const [nameFilter, setNameFilter] = useState("");
  const [staffPositionFilter, setStaffPositionFilter] = useState("");
  const [filteredRoster, setFilteredRoster] = useState(dutyRoster);

  // Apply filters when they change or when the roster changes
  useEffect(() => {
    let filtered = dutyRoster || [];

    // Apply name filter
    if (nameFilter) {
      const lowerCaseFilter = nameFilter.toLowerCase();
      filtered = filtered.filter((roster) => {
        const staffName =
          roster.staffDetails?.fullName ||
          roster.staffId?.fullName ||
          roster.name ||
          "";
        return staffName.toLowerCase().includes(lowerCaseFilter);
      });
    }

    // Apply staff position filter
    if (staffPositionFilter) {
      filtered = filtered.filter(
        (roster) => roster.staffPosition === staffPositionFilter,
      );
    }

    setFilteredRoster(filtered);
  }, [nameFilter, staffPositionFilter, dutyRoster]);

  // Transform duty rosters into calendar events
  const events = filteredRoster.map((roster) => {
    const staffName =
      roster.staffDetails?.fullName ||
      roster.staffId?.fullName ||
      roster.name ||
      "Unknown Staff";

    const masterWorkplanTitle =
      roster.masterWorkplanDetails?.title ||
      roster.masterWorkplanId?.title ||
      "Unknown Workplan";

    return {
      id: roster._id,
      title: `${staffName} - ${masterWorkplanTitle}`,
      start: new Date(roster.startDate),
      end: new Date(roster.endDate),
      resource: roster,
      allDay: true,
    };
  });

  // Custom event component with status indicators
  const EventComponent = ({ event }) => {
    const roster = event.resource;
    const staffName =
      roster.staffDetails?.fullName ||
      roster.staffId?.fullName ||
      roster.name ||
      "Unknown Staff";

    const isActive =
      new Date() >= new Date(roster.startDate) &&
      new Date() <= new Date(roster.endDate);

    return (
      <div className="duty-event">
        <div className="d-flex align-items-center justify-content-between">
          <span
            className="staff-name"
            style={{ fontSize: "11px", fontWeight: "bold" }}
          >
            {staffName}
          </span>
          <div className="status-indicators">
            <Badge
              value={roster.staffPosition}
              severity={
                roster.staffPosition === "Head"
                  ? "danger"
                  : roster.staffPosition === "Deputy"
                    ? "warning"
                    : roster.staffPosition === "Assistant"
                      ? "info"
                      : "success"
              }
              style={{ fontSize: "8px", marginRight: "2px" }}
            />
            <Badge
              value={isActive ? "ACTIVE" : "INACTIVE"}
              severity={isActive ? "success" : "secondary"}
              style={{ fontSize: "8px" }}
            />
          </div>
        </div>
        <div style={{ fontSize: "9px", color: "#fff", opacity: 0.9 }}>
          {roster.masterWorkplanDetails?.title || "Workplan"}
        </div>
      </div>
    );
  };

  // Handle event selection
  const handleSelectEvent = (event) => {
    setSelectedRoster(event.resource);
    setShowRosterDialog(true);
  };

  // Handle date selection
  const handleSelectSlot = (slotInfo) => {
    const selectedDate = moment(slotInfo.start).format("YYYY-MM-DD");
    const rostersForDate = filteredRoster.filter((roster) => {
      const rosterStart = moment(roster.startDate).format("YYYY-MM-DD");
      const rosterEnd = moment(roster.endDate).format("YYYY-MM-DD");
      return selectedDate >= rosterStart && selectedDate <= rosterEnd;
    });

    setSelectedDate(slotInfo.start);
    setDayRosters(rostersForDate);
    setShowDayDialog(true);
  };

  // Custom day cell component to show roster count
  const DayCellWrapper = ({ children, value }) => {
    const dayRosters = filteredRoster.filter((roster) => {
      const dayDate = moment(value).format("YYYY-MM-DD");
      const rosterStart = moment(roster.startDate).format("YYYY-MM-DD");
      const rosterEnd = moment(roster.endDate).format("YYYY-MM-DD");
      return dayDate >= rosterStart && dayDate <= rosterEnd;
    });

    return (
      <div className="rbc-day-bg">
        {children}
        {dayRosters.length > 0 && (
          <div
            className="duty-day-summary"
            style={{
              position: "absolute",
              bottom: "2px",
              right: "2px",
              fontSize: "9px",
              backgroundColor: "rgba(0,123,255,0.8)",
              color: "white",
              padding: "1px 4px",
              borderRadius: "3px",
              fontWeight: "bold",
            }}
          >
            {dayRosters.length} staff
          </div>
        )}
      </div>
    );
  };

  // Format date
  const formatDate = (date) => {
    return moment(date).format("MMMM Do, YYYY");
  };

  // Get staff name from roster
  const getStaffName = (roster) => {
    return (
      roster.staffDetails?.fullName ||
      roster.staffId?.fullName ||
      roster.name ||
      "Unknown Staff"
    );
  };

  // Get master workplan name
  const getMasterWorkplanName = (roster) => {
    return (
      roster.masterWorkplanDetails?.title ||
      roster.masterWorkplanId?.title ||
      "Unknown Workplan"
    );
  };

  // Get status description
  const getStatusDescription = (status) => {
    const statusMap = {
      ON: "On Duty",
      OFF: "Off Duty",
      AL: "Annual Leave",
      CL: "Casual Leave",
      "ML/PL": "Medical/Paternity Leave",
      PH: "Public Holiday",
      UI: "Unplanned Issues",
    };
    return statusMap[status] || status;
  };

  // Get unique staff positions for filter
  const getUniqueStaffPositions = () => {
    const positions = filteredRoster
      .map((roster) => roster.staffPosition)
      .filter(Boolean);
    return [...new Set(positions)];
  };

  return (
    <div className="row">
      <div className="col-lg-12">
        <div className="card">
          <div className="card-body">
            <style jsx>{`
              .duty-calendar {
                height: 700px;
                padding: 20px;
              }
              .rbc-calendar {
                height: 100%;
              }
              .duty-event {
                background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
                border: none;
                border-radius: 6px;
                padding: 4px 6px;
                color: white;
                font-size: 11px;
                margin: 1px 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
              }
              .duty-event:hover {
                background: linear-gradient(135deg, #0056b3 0%, #007bff 100%);
                transform: translateY(-1px);
                box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
              }
              .rbc-day-bg {
                position: relative;
              }
              .duty-day-summary {
                z-index: 1;
              }
              .rbc-event {
                background: none !important;
                border: none !important;
                padding: 0 !important;
              }
              .rbc-event-content {
                padding: 0 !important;
              }
              .calendar-legend {
                background: #f8f9fa;
                border-radius: 8px;
                padding: 10px;
                margin-bottom: 15px;
              }
              .legend-item {
                display: flex;
                align-items: center;
                font-size: 12px;
              }
              .calendar-controls .btn {
                margin-right: 5px;
              }
              .rbc-header {
                padding: 10px 5px;
                font-weight: 600;
                background: #f8f9fa;
                border-bottom: 1px solid #dee2e6;
              }
              .rbc-today {
                background-color: #e3f2fd;
              }
              .rbc-off-range-bg {
                background-color: #fafafa;
              }
              .status-indicators {
                display: flex;
                gap: 2px;
              }
              .weekly-schedule-grid {
                display: grid;
                grid-template-columns: repeat(7, 1fr);
                gap: 5px;
                margin-top: 10px;
              }
              .day-schedule {
                border: 1px solid #dee2e6;
                border-radius: 4px;
                padding: 8px;
                text-align: center;
                font-size: 12px;
              }
              .day-schedule.on-duty {
                background-color: #d4edda;
                border-color: #c3e6cb;
              }
              .day-schedule.off-duty {
                background-color: #f8d7da;
                border-color: #f5c6cb;
              }
            `}</style>

            <div className="calendar-header mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">
                  <i className="ti ti-calendar me-2"></i>
                  Duty Roster Calendar
                </h4>
                <div className="calendar-controls">
                  <button
                    className="btn btn-outline-primary btn-sm me-2"
                    onClick={() => setDate(new Date())}
                  >
                    <i className="ti ti-current-location me-1"></i>
                    Today
                  </button>
                  <button
                    className={`btn btn-sm me-1 ${view === "month" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setView("month")}
                  >
                    Month
                  </button>
                  <button
                    className={`btn btn-sm me-1 ${view === "week" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setView("week")}
                  >
                    Week
                  </button>
                  <button
                    className={`btn btn-sm ${view === "day" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setView("day")}
                  >
                    Day
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label">Filter by Staff Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search staff name..."
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Filter by Position</label>
                  <select
                    className="form-select"
                    value={staffPositionFilter}
                    onChange={(e) => setStaffPositionFilter(e.target.value)}
                  >
                    <option value="">All Positions</option>
                    {getUniqueStaffPositions().map((position) => (
                      <option key={position} value={position}>
                        {position}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setNameFilter("");
                      setStaffPositionFilter("");
                    }}
                  >
                    <i className="ti ti-refresh me-1"></i>
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Legend */}
              <div className="calendar-legend">
                <div className="row">
                  <div className="col-12">
                    <div className="d-flex flex-wrap align-items-center gap-4">
                      <div className="legend-item">
                        <span className="badge bg-danger me-2">HEAD</span>
                        <span>Head Position</span>
                      </div>
                      <div className="legend-item">
                        <span className="badge bg-warning me-2">DEPUTY</span>
                        <span>Deputy Position</span>
                      </div>
                      <div className="legend-item">
                        <span className="badge bg-info me-2">ASSISTANT</span>
                        <span>Assistant Position</span>
                      </div>
                      <div className="legend-item">
                        <span className="badge bg-success me-2">NORMAL</span>
                        <span>Normal Position</span>
                      </div>
                      <div className="legend-item">
                        <span className="badge bg-success me-2">ACTIVE</span>
                        <span>Currently Active</span>
                      </div>
                      <div className="legend-item">
                        <span className="badge bg-secondary me-2">
                          INACTIVE
                        </span>
                        <span>Currently Inactive</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="duty-calendar">
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                style={{ height: "100%" }}
                view={view}
                onView={setView}
                date={date}
                onNavigate={setDate}
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                selectable
                components={{
                  event: EventComponent,
                  dateCellWrapper: DayCellWrapper,
                }}
                eventPropGetter={(event) => ({
                  className: "duty-calendar-event",
                  style: {
                    backgroundColor: "transparent",
                    border: "none",
                  },
                })}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Duty Roster Details Dialog */}
      <Dialog
        header="Duty Roster Details"
        visible={showRosterDialog}
        onHide={() => setShowRosterDialog(false)}
        style={{ width: "700px" }}
        modal
      >
        {selectedRoster && (
          <div className="roster-details">
            <div className="row">
              <div className="col-12">
                <div className="card mb-3">
                  <div className="card-body">
                    <h5 className="card-title">
                      {getStaffName(selectedRoster)}
                    </h5>
                    <div className="row">
                      <div className="col-md-6">
                        <p className="mb-2">
                          <strong>Position:</strong>{" "}
                          {selectedRoster.staffPosition}
                        </p>
                        <p className="mb-2">
                          <strong>Master Workplan:</strong>{" "}
                          {getMasterWorkplanName(selectedRoster)}
                        </p>
                        <p className="mb-2">
                          <strong>Start Date:</strong>{" "}
                          {formatDate(selectedRoster.startDate)}
                        </p>
                        <p className="mb-2">
                          <strong>End Date:</strong>{" "}
                          {formatDate(selectedRoster.endDate)}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-2">
                          <strong>Status:</strong>
                          <span
                            className={`badge ms-2 ${
                              new Date() >=
                                new Date(selectedRoster.startDate) &&
                              new Date() <= new Date(selectedRoster.endDate)
                                ? "bg-success"
                                : "bg-secondary"
                            }`}
                          >
                            {new Date() >= new Date(selectedRoster.startDate) &&
                            new Date() <= new Date(selectedRoster.endDate)
                              ? "ACTIVE"
                              : "INACTIVE"}
                          </span>
                        </p>
                        <p className="mb-2">
                          <strong>Duration:</strong>{" "}
                          {Math.ceil(
                            (new Date(selectedRoster.endDate) -
                              new Date(selectedRoster.startDate)) /
                              (1000 * 60 * 60 * 24),
                          )}{" "}
                          days
                        </p>
                        <p className="mb-2">
                          <strong>Exceptions:</strong>{" "}
                          {selectedRoster.exceptions?.length || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Weekly Schedule */}
                <div className="card mb-3">
                  <div className="card-header">
                    <h6 className="mb-0">Weekly Schedule</h6>
                  </div>
                  <div className="card-body">
                    <div className="weekly-schedule-grid">
                      {[
                        "monday",
                        "tuesday",
                        "wednesday",
                        "thursday",
                        "friday",
                        "saturday",
                        "sunday",
                      ].map((day) => {
                        const daySchedule =
                          selectedRoster.weeklySchedule?.[day] || [];
                        const hasOnDutySlots =
                          Array.isArray(daySchedule) &&
                          daySchedule.some((slot) => slot.status === "ON");

                        return (
                          <div
                            key={day}
                            className={`day-schedule ${hasOnDutySlots ? "on-duty" : "off-duty"}`}
                          >
                            <div className="fw-bold">
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </div>
                            {Array.isArray(daySchedule) &&
                            daySchedule.length > 0 ? (
                              daySchedule.map((slot, index) => (
                                <div key={index} style={{ fontSize: "10px" }}>
                                  {slot.startTime && slot.endTime
                                    ? `${slot.startTime}-${slot.endTime}`
                                    : getStatusDescription(slot.status)}
                                </div>
                              ))
                            ) : (
                              <div style={{ fontSize: "10px" }}>Off</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Exceptions */}
                {selectedRoster.exceptions &&
                  selectedRoster.exceptions.length > 0 && (
                    <div className="card">
                      <div className="card-header">
                        <h6 className="mb-0">Exceptions</h6>
                      </div>
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead>
                              <tr>
                                <th>Date</th>
                                <th>Time</th>
                                <th>Status</th>
                                <th>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedRoster.exceptions.map(
                                (exception, index) => (
                                  <tr key={index}>
                                    <td>{formatDate(exception.date)}</td>
                                    <td>
                                      {exception.startTime && exception.endTime
                                        ? `${exception.startTime} - ${exception.endTime}`
                                        : "All Day"}
                                    </td>
                                    <td>
                                      <span className="badge bg-info">
                                        {getStatusDescription(exception.status)}
                                      </span>
                                    </td>
                                    <td>
                                      {exception.reason || "No reason provided"}
                                    </td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* Day Rosters Dialog */}
      <Dialog
        header={`Duty Rosters for ${selectedDate ? formatDate(selectedDate) : ""}`}
        visible={showDayDialog}
        onHide={() => setShowDayDialog(false)}
        style={{ width: "800px" }}
        modal
      >
        <div className="day-rosters">
          {dayRosters.length === 0 ? (
            <div className="text-center py-4">
              <i
                className="ti ti-calendar-off"
                style={{ fontSize: "48px", color: "#ccc" }}
              ></i>
              <p className="mt-2">No duty rosters for this date.</p>
            </div>
          ) : (
            <div>
              <div className="day-summary mb-4">
                <div className="row">
                  <div className="col-md-3">
                    <div className="card text-center bg-primary text-white">
                      <div className="card-body">
                        <h6 className="mb-1">Total Staff</h6>
                        <h3 className="mb-0">{dayRosters.length}</h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center bg-success text-white">
                      <div className="card-body">
                        <h6 className="mb-1">Active</h6>
                        <h3 className="mb-0">
                          {
                            dayRosters.filter(
                              (roster) =>
                                new Date() >= new Date(roster.startDate) &&
                                new Date() <= new Date(roster.endDate),
                            ).length
                          }
                        </h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center bg-warning text-white">
                      <div className="card-body">
                        <h6 className="mb-1">Head/Deputy</h6>
                        <h3 className="mb-0">
                          {
                            dayRosters.filter((roster) =>
                              ["Head", "Deputy"].includes(roster.staffPosition),
                            ).length
                          }
                        </h3>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card text-center bg-info text-white">
                      <div className="card-body">
                        <h6 className="mb-1">Workplans</h6>
                        <h3 className="mb-0">
                          {
                            new Set(
                              dayRosters.map(
                                (roster) => roster.masterWorkplanId,
                              ),
                            ).size
                          }
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rosters-list">
                {dayRosters.map((roster) => (
                  <div key={roster._id} className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{getStaffName(roster)}</h6>
                          <p className="mb-1 text-primary fw-bold">
                            {getMasterWorkplanName(roster)}
                          </p>
                          <small className="text-muted">
                            {formatDate(roster.startDate)} -{" "}
                            {formatDate(roster.endDate)}
                          </small>
                        </div>
                        <div className="text-end">
                          <span
                            className={`badge mb-1 ${
                              roster.staffPosition === "Head"
                                ? "bg-danger"
                                : roster.staffPosition === "Deputy"
                                  ? "bg-warning"
                                  : roster.staffPosition === "Assistant"
                                    ? "bg-info"
                                    : "bg-success"
                            }`}
                          >
                            {roster.staffPosition}
                          </span>
                          <br />
                          <span
                            className={`badge ${
                              new Date() >= new Date(roster.startDate) &&
                              new Date() <= new Date(roster.endDate)
                                ? "bg-success"
                                : "bg-secondary"
                            }`}
                          >
                            {new Date() >= new Date(roster.startDate) &&
                            new Date() <= new Date(roster.endDate)
                              ? "ACTIVE"
                              : "INACTIVE"}
                          </span>
                        </div>
                      </div>
                      <div className="mt-3">
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setSelectedRoster(roster);
                            setShowDayDialog(false);
                            setShowRosterDialog(true);
                          }}
                        >
                          <i className="ti ti-eye me-1"></i>
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
};

export default DutyRosterCalendar;
